import { cartFreeItemRememberParameters } from '../../helpers/FreeItemCookies';

const cartTriggerSelector = '.twc-hdr-cart';
const cartCountSelector = '.twc-hdr-cart-total-count';

let appMainReady = null;
let cartTogglePending = false;

/**
 * Returns the cart quantity available in the server-rendered bootstrap data.
 * This mirrors CartV3's count by excluding Redo service lines.
 */
export const getBootstrapCartCount = () => {
  const items = window.cartDataJson?.items;

  if (Array.isArray(items)) {
    return items.reduce((count, item) => {
      if (item?.vendor === 're:do') {
        return count;
      }

      return count + Number(item?.quantity || 0);
    }, 0);
  }

  return Number(
    window.cartBootstrap?.item_count || window.cartDataJson?.item_count || 0
  );
};

/**
 * Paints the header badge without loading React, Redux, or CartV3.
 */
export const updateBootstrapCartCount = () => {
  const count = getBootstrapCartCount();

  document.querySelectorAll(cartCountSelector).forEach((counter) => {
    counter.setAttribute('data-count', count);
    counter.textContent = count;
  });
};

/**
 * Persists parameter-based GWP activation before the lazy CartV3 bundle loads.
 * This keeps landing-page and quick-cart journeys equivalent to full CartV3.
 */
export const rememberBootstrapFreeItemParameters = () => {
  const configuredItems = window.cartDataJson?.free_item_data?.byParameter;
  const legacyItems = window.cartData?.parameters;

  return cartFreeItemRememberParameters(
    Array.isArray(configuredItems)
      ? configuredItems
      : Array.isArray(legacyItems)
        ? legacyItems
        : []
  );
};

/**
 * Loads the storefront React application once. CartV3 remains available on
 * every route, but non-product pages do not download it until cart intent.
 */
export const loadAppMain = () => {
  if (!appMainReady) {
    if (!document.querySelector('#app')) {
      return Promise.reject(new Error('AppMain root is unavailable'));
    }

    appMainReady = import(
      /* webpackChunkName: "AppMain_CartV3" */ './components/App/App'
    ).catch((error) => {
      appMainReady = null;
      throw error;
    });
  }

  return appMainReady;
};

const getCartTrigger = (event) =>
  event.target?.closest?.(cartTriggerSelector) || null;

/**
 * Resolves a non-empty cart URL even when the trigger is not an anchor.
 */
export const getCartFallbackUrl = (trigger) => {
  const candidates = [
    trigger?.getAttribute?.('href'),
    trigger?.href,
    window.cartBootstrap?.cart_url,
    '/cart',
  ];

  return candidates.find(
    (candidate) =>
      typeof candidate === 'string' &&
      candidate.trim().length > 0 &&
      candidate.trim() !== '#'
  );
};

const preloadCartOnIntent = (event) => {
  if (!getCartTrigger(event)) {
    return;
  }

  loadAppMain().catch(() => {});
};

const toggleCartOnIntent = (event) => {
  const trigger = getCartTrigger(event);
  const fullCart = document.querySelector('#cart[data-view="full"]');

  if (!trigger || fullCart || cartTogglePending) {
    return;
  }

  event.preventDefault();
  event.stopImmediatePropagation();
  cartTogglePending = true;

  loadAppMain()
    .then(({ toggleCart }) => toggleCart())
    .catch(() => {
      window.location.assign(getCartFallbackUrl(trigger));
    })
    .finally(() => {
      cartTogglePending = false;
    });
};

/**
 * Starts the small global bridge. Product and full-cart routes still require
 * AppMain immediately; other routes load it on hover, focus, or click.
 */
export const initializeAppMainBootstrap = () => {
  rememberBootstrapFreeItemParameters();
  updateBootstrapCartCount();

  document.addEventListener('pointerover', preloadCartOnIntent, true);
  document.addEventListener('focusin', preloadCartOnIntent, true);
  document.addEventListener('click', toggleCartOnIntent, true);

  if (
    document.body.classList.contains('template-product') ||
    document.querySelector('#product-full, #cart[data-view="full"]')
  ) {
    loadAppMain().catch(() => {});
  }
};
